import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import Hls from 'hls.js';

const HlsPlayer = forwardRef(({
  url,
  playing,
  muted,
  volume,
  playbackRate,
  onProgress,
  onReady,
  onError,
  onBufferEnd,
  onStart,
  onPlay,
  onEnded,
  className,
  width,
  height,
  style,
}, ref) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const hasStartedRef = useRef(false);
  const progressIntervalRef = useRef(null);

  // Only use crossOrigin for HLS streams (needed for manifest/segments)
  const isHlsUrl = url?.includes('.m3u8');

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    seekTo: (amount) => {
      if (videoRef.current) {
        // Match ReactPlayer behavior: values 0-1 are treated as fractions of duration
        if (amount >= 0 && amount <= 1) {
          videoRef.current.currentTime = amount * (videoRef.current.duration || 0);
        } else {
          videoRef.current.currentTime = amount;
        }
      }
    },
    getCurrentTime: () => videoRef.current?.currentTime || 0,
    getDuration: () => videoRef.current?.duration || 0,
    getInternalPlayer: () => videoRef.current,
  }));

  // Initialize HLS
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !url) return;

    // Check if it's an HLS stream
    const isHls = url.includes('.m3u8');

    if (isHls && Hls.isSupported()) {
      // Use HLS.js for browsers that don't support native HLS
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        backBufferLength: 90,
        maxBufferLength: 30,
        maxMaxBufferLength: 600,
        // Don't manipulate audio timing
        nudgeOffset: 0.1,
        nudgeMaxRetry: 3,
      });

      hlsRef.current = hls;

      hls.loadSource(url);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        // eslint-disable-next-line no-console
        console.log('🎬 [HLS DEBUG] Manifest parsed - HLS ready');
        if (onReady) onReady();
        if (playing) {
          video.play().catch(() => {});
        }
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        // eslint-disable-next-line no-console
        console.log('🎬 [HLS DEBUG] HLS Error:', { fatal: data.fatal, type: data.type, details: data.details });
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              hls.recoverMediaError();
              break;
            default:
              if (onError) onError(data);
              break;
          }
        }
      });

      return () => {
        hls.destroy();
        hlsRef.current = null;
      };
    }

    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Native HLS support (Safari)
      video.src = url;
      video.addEventListener('loadedmetadata', () => {
        if (onReady) onReady();
        if (playing) {
          video.play().catch(() => {});
        }
      });
    } else {
      // Regular video file
      video.src = url;
      video.addEventListener('loadedmetadata', () => {
        if (onReady) onReady();
      });
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [url]);

  // Handle play/pause
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (playing) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [playing]);

  // Handle volume
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.volume = volume;
  }, [volume]);

  // Handle muted
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = muted;
  }, [muted]);

  // Handle playback rate
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.playbackRate = playbackRate;
  }, [playbackRate]);

  // Progress tracking
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    progressIntervalRef.current = setInterval(() => {
      if (video.duration && onProgress) {
        onProgress({
          played: video.currentTime / video.duration,
          playedSeconds: video.currentTime,
          loaded: video.buffered.length > 0
            ? video.buffered.end(video.buffered.length - 1) / video.duration
            : 0,
          loadedSeconds: video.buffered.length > 0
            ? video.buffered.end(video.buffered.length - 1)
            : 0,
        });
      }
    }, 100);

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [onProgress]);

  // Event handlers
  const handlePlay = () => {
    // eslint-disable-next-line no-console
    console.log('🎬 [HLS DEBUG] handlePlay (onPlay event) fired');
    if (!hasStartedRef.current) {
      hasStartedRef.current = true;
      if (onStart) onStart();
    }
    // Don't call onPlay here - wait for onPlaying event
  };

  // Called when video is actually playing with frames ready
  const handlePlaying = () => {
    // eslint-disable-next-line no-console
    // console.log('🎬 [HLS DEBUG] handlePlaying (onPlaying event) fired - calling parent onPlay');
    if (onPlay) onPlay();
  };

  const handleCanPlay = () => {
    // eslint-disable-next-line no-console
    // console.log('🎬 [HLS DEBUG] handleCanPlay fired - video can start playing');
    if (onBufferEnd) onBufferEnd();
  };

  const handleEnded = () => {
    if (onEnded) onEnded();
  };

  const handleError = (e) => {
    if (onError) onError(e);
  };

  return (
    <video
      ref={videoRef}
      className={className}
      width={width}
      height={height}
      style={style}
      onPlay={handlePlay}
      onPlaying={handlePlaying}
      onCanPlay={handleCanPlay}
      onEnded={handleEnded}
      onError={handleError}
      playsInline
      {...(isHlsUrl && { crossOrigin: 'anonymous' })}
    />
  );
});

HlsPlayer.displayName = 'HlsPlayer';

export default HlsPlayer;
